// Cart builder section: sell more seats on an email subscription the account
// already has. Account carts, card checkout only (the modal does not render it
// for a prospect or an invoice).
//
// Pick the subscription (from the account's products) and how many seats to
// add. The price shown is squish-backend's quote, what is due today for the
// rest of the period, and the line added is the one it served
// (src/lib/emailSeats.js).
import { useEffect, useState } from 'react';
import { listAccountProducts, getEmailChangeOptions } from '../api';
import { seatableEmailSubs, withEmailSeats, emailSeatsBlocker, seatsSummary, MAX_SEAT_STEP } from '../lib/emailSeats';

export default function EmailSeatsSection({ accountId, isInvoice, isProspect, items, setItems, ui }) {
  const { sectionLabel, miniSelect, addBtn, fmt } = ui;
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState(null);     // null loading | { error } | subs[]
  const [subId, setSubId] = useState('');
  const [addSeats, setAddSeats] = useState(1);
  const [quote, setQuote] = useState(null);   // { key, ...seatsQuote } | { key, error }
  const [quoteTry, setQuoteTry] = useState(0); // bumped by Retry

  const sub = Array.isArray(subs) ? (subs.find(s => s.subscriptionId === subId) || subs[0] || null) : null;
  const count = Math.min(Math.max(1, addSeats || 1), MAX_SEAT_STEP);
  const quoteKey = sub ? `${sub.subscriptionId}|${count}|${quoteTry}` : '';

  // The account's subscriptions, once the rep opens the section.
  useEffect(() => {
    if (!open || isProspect || !accountId || subs != null) return;
    let cancelled = false;
    listAccountProducts(accountId)
      .then(r => {
        if (cancelled) return;
        const list = seatableEmailSubs(r.products);
        // A short list (one adapter failed) is not proof there is nothing to sell.
        setSubs(list.length === 0 && r.productsComplete === false
          ? { error: 'some of their products could not be read' }
          : list);
      })
      .catch(e => { if (!cancelled) setSubs({ error: e.message || 'request failed' }); });
    return () => { cancelled = true; };
  }, [open, isProspect, accountId, subs]);

  // What that many more seats cost today, from squish-backend.
  useEffect(() => {
    if (!quoteKey) return;
    let cancelled = false;
    const [sid, n] = quoteKey.split('|');
    const t = setTimeout(() => {
      getEmailChangeOptions(accountId, sid, { addSeats: Number(n) })
        .then(r => { if (!cancelled) setQuote({ key: quoteKey, ...(r.seatsQuote || { error: 'no quote' }) }); })
        .catch(e => { if (!cancelled) setQuote({ key: quoteKey, error: e.message || 'request failed', failed: true }); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [accountId, quoteKey]);

  const q = quote?.key === quoteKey ? quote : null;
  const line = q && !q.error ? q.line || null : null;
  const blocker = emailSeatsBlocker({ isInvoice, isProspect, subs, items, line });
  const reason = sub && !q ? 'Pricing…' : q?.error || null;

  const note = { fontSize: 11, color: 'var(--text3)', lineHeight: 1.4 };
  const wide = { ...miniSelect, width: '100%', fontSize: 12, padding: '6px 8px' };
  const ghost = { ...addBtn, background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)' };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ ...sectionLabel, margin: 0 }}>Email seats</div>
        {!open && <button onClick={() => setOpen(true)} style={ghost}>Add seats</button>}
      </div>
      {open && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg3)', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.isArray(subs) && subs.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={sub?.subscriptionId || ''} onChange={e => setSubId(e.target.value)} style={{ ...wide, flex: 1 }}>
                {subs.map(s => (
                  <option key={s.subscriptionId} value={s.subscriptionId}>{s.label}{s.domain ? ` · ${s.domain}` : ''}</option>
                ))}
              </select>
              <input
                type="number" min={1} max={MAX_SEAT_STEP} value={count}
                onChange={e => setAddSeats(parseInt(e.target.value, 10) || 1)}
                title={`Seats to add, up to ${MAX_SEAT_STEP} at a time`}
                style={{ ...miniSelect, width: 56, fontSize: 12, padding: '6px 8px' }}
              />
            </div>
          )}
          {line && <div style={note}>{seatsSummary(q)}</div>}

          {blocker || reason ? (
            <div style={note}>
              {blocker || reason}
              {(subs?.error || q?.failed) && (
                <button onClick={() => (subs?.error ? setSubs(null) : setQuoteTry(n => n + 1))} style={{ ...ghost, marginLeft: 8 }}>Retry</button>
              )}
            </div>
          ) : (
            <button onClick={() => line && setItems(prev => withEmailSeats(prev, line))} disabled={!line} style={{ ...addBtn, width: '100%', marginTop: 2, opacity: line ? 1 : 0.5 }}>
              + Add {line?.productName} · {fmt(line?.unitPrice || 0)} today
            </button>
          )}

          <div style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.4 }}>
            Adds the seats at Microsoft and on the subscription once the cart is paid; they then bill with it. Today's price covers the rest of this period and shrinks daily, so a link paid days later can be refused and need a new cart. Seats can't be removed until renewal.
          </div>
        </div>
      )}
    </>
  );
}
